<template>
  <div>
    <app-navigation />
    <main class="admin-page">
      <div class="admin-heading">
        <span class="admin-kicker">Site hierarchy</span>
        <h1>Sites</h1>
        <p>
          Managed root: <strong>{{ root.name }}</strong> · {{ root.id }}
        </p>
      </div>
      <div v-if="error" class="admin-alert bad">{{ error }}</div>
      <div v-if="notice" class="admin-alert good">{{ notice }}</div>
      <section class="admin-grid">
        <div class="admin-card">
          <h2>Create child site</h2>
          <form @submit.prevent="create">
            <label>Name<input v-model.trim="createForm.name" required /></label
            ><label
              >Slug<input
                v-model.trim="createForm.slug"
                pattern="[a-z0-9-]+"
                required /></label
            ><label
              >Approved template version<select
                v-model="createForm.template_version_id"
                required
              >
                <option value="">Select version</option>
                <option v-for="v in versions" :key="v.id" :value="v.id">
                  {{ templateName(v.template_id) }} · {{ v.version }}
                </option>
              </select></label
            ><label class="admin-check"
              ><input v-model="overrideSeed" type="checkbox" /> Override the
              template seed data for this site</label
            ><label v-if="overrideSeed"
              >Replacement seed JSON<textarea
                v-model="customSeed"
                rows="14"
                spellcheck="false"
              ></textarea></label
            ><p v-if="overrideSeed" class="admin-help">
              Paste an array of records containing collection, key, and an
              object value. Template seed import will remain unused.
            </p
            ><router-link to="/templates" class="admin-link"
              >Browse template catalog</router-link
            ><button class="admin-primary">
              Create beneath {{ root.name }}
            </button>
          </form>
        </div>
        <div class="admin-card">
          <h2>Authorized sites</h2>
          <select v-model="selected" @change="loadSite">
            <option :value="root.id">{{ root.name }} (Admin root)</option>
            <option v-for="site in children" :key="site.id" :value="site.id">
              {{ site.name }}
            </option>
          </select>
          <div v-if="detail" class="admin-context">
            <strong>{{ detail.name }}</strong
            ><code>{{ detail.id }}</code
            ><span
              >{{ detail.status }} ·
              {{
                detail.active_deployment_id
                  ? "active deployment"
                  : "not deployed"
              }}</span
            >
          </div>
        </div>
      </section>
      <section v-if="detail" class="admin-card">
        <h2>Site settings</h2>
        <div class="admin-form-grid">
          <label>Name<input v-model="detail.name" /></label
          ><label>Status<input v-model="detail.status" disabled /></label
          ><label
            >Current template<input
              :value="
                currentVersion
                  ? templateName(currentVersion.template_id)
                  : 'No template'
              "
              disabled /></label
          ><label
            >Current template version<input
              :value="
                currentVersion?.version ||
                optionView?.template_version ||
                'Unknown'
              "
              disabled
          /></label>
        </div>
        <div v-if="currentVersion" class="admin-context">
          <label class="grow"
            >Upgrade within {{ templateName(currentVersion.template_id)
            }}<select v-model="upgradeVersionId">
              <option value="">Choose an authorized version</option>
              <option v-for="v in upgradeVersions" :key="v.id" :value="v.id">
                {{ v.version }}
              </option>
            </select></label
          ><button
            class="admin-secondary"
            :disabled="!upgradeVersionId"
            @click="upgrade"
          >
            Upgrade template version</button
          ><span v-if="!upgradeVersions.length"
            >This site already uses the only authorized version.</span
          >
        </div>
        <button
          class="admin-primary"
          :disabled="siteSaving"
          @click="saveSite"
        >
          {{
            siteSaving ? "Saving…" : siteSavedAt ? "Saved ✓" : "Save site record"
          }}</button
        ><span v-if="siteSavedAt" class="admin-status"
          >Saved {{ siteSavedAt.toLocaleTimeString() }}</span
        ><button
          v-if="detail.id !== root.id"
          class="admin-danger"
          @click="archive"
        >
          Permanently delete child
        </button>
      </section>
      <section v-if="optionView" class="admin-card">
        <div class="admin-row">
          <div>
            <h2>Template options</h2>
            <p>
              Template {{ optionView.template_version }} · snapshot
              {{ optionView.version }}
            </p>
          </div>
          <button class="admin-secondary" @click="advanced = !advanced">
            {{ advanced ? "Use form" : "Advanced JSON" }}
          </button>
        </div>
        <textarea v-if="advanced" v-model="rawOptions" rows="14"></textarea>
        <div v-else class="admin-form-grid">
          <div v-for="field in fields" :key="field.key" class="admin-option">
            <div class="admin-row">
              <label>{{ field.schema.title || field.key }}</label
              ><small>{{
                isOverride(field.key) ? "Overridden" : "Inherited"
              }}</small>
            </div>
            <input
              v-if="field.schema.type === 'boolean'"
              type="checkbox"
              :checked="Boolean(value(field.key))"
              @change="set(field.key, $event.target.checked)"
            /><select
              v-else-if="field.schema.enum"
              :value="value(field.key)"
              @change="set(field.key, $event.target.value)"
            >
              <option v-for="item in field.schema.enum" :key="item">
                {{ item }}
              </option></select
            ><textarea
              v-else-if="['array', 'object'].includes(field.schema.type)"
              :value="complex(field.key)"
              rows="5"
              @change="setComplex(field.key, $event.target.value)"
            ></textarea
            ><input
              v-else
              :type="
                ['number', 'integer'].includes(field.schema.type)
                  ? 'number'
                  : 'text'
              "
              :value="value(field.key)"
              @input="
                set(
                  field.key,
                  ['number', 'integer'].includes(field.schema.type)
                    ? Number($event.target.value)
                    : $event.target.value
                )
              "
            /><button
              v-if="isOverride(field.key)"
              class="admin-link"
              @click="reset(field.key)"
            >
              Reset to inherited
            </button>
          </div>
        </div>
        <button class="admin-primary" @click="saveOptions">
          Save sparse overrides
        </button>
      </section>
    </main>
  </div>
</template>
<script>
import { session } from "../platform/session";
import * as api from "../platform/api";
export default {
  name: "Sites",
  data() {
    return {
      root: session.root,
      children: [],
      templates: [],
      versions: [],
      selected: session.root.id,
      detail: null,
      optionView: null,
      overrides: {},
      rawOptions: "{}",
      advanced: false,
      upgradeVersionId: "",
      error: "",
      notice: "",
      siteSaving: false,
      siteSavedAt: null,
      overrideSeed: false,
      customSeed:
        '[\n  {"collection":"businesses","key":"example","value":{"name":"Example Business"}}\n]',
      createForm: {
        name: "",
        slug: "",
        template_version_id: this.$route.query.templateVersion || "",
      },
    };
  },
  computed: {
    fields() {
      return Object.entries(
        this.optionView?.options_schema?.properties || {}
      ).map(([key, schema]) => ({ key, schema }));
    },
    currentVersion() {
      const id =
        this.optionView?.template_version_id ||
        this.detail?.template_version_id;
      return this.versions.find((v) => v.id === id);
    },
    upgradeVersions() {
      return this.currentVersion
        ? this.versions.filter(
            (v) =>
              v.template_id === this.currentVersion.template_id &&
              v.id !== this.currentVersion.id
          )
        : [];
    },
  },
  async created() {
    await this.reload();
    await this.loadSite();
  },
  methods: {
    async perform(fn, message) {
      this.error = "";
      this.notice = "";
      try {
        await fn();
        this.notice = message;
      } catch (e) {
        this.error = e.message;
      }
    },
    async reload() {
      const [children, catalog] = await Promise.all([
        api.children(this.root.id, this.root.id),
        api.authorizedTemplates(this.root.id),
      ]);
      this.children = children;
      this.templates = catalog.items;
      this.versions = this.templates.flatMap((t) =>
        (t.versions || []).map((v) => ({ ...v, template_id: t.id }))
      );
    },
    templateName(id) {
      return this.templates.find((t) => t.id === id)?.name || id;
    },
    async loadSite() {
      await this.perform(async () => {
        this.detail = await api.getSite(this.selected, this.root.id);
        this.optionView = await api.options(this.selected, this.root.id);
        this.overrides = { ...this.optionView.values };
        this.rawOptions = JSON.stringify(this.overrides, null, 2);
        this.upgradeVersionId = "";
      }, "Site loaded");
    },
    isOverride(k) {
      return Object.prototype.hasOwnProperty.call(this.overrides, k);
    },
    value(k) {
      return this.isOverride(k)
        ? this.overrides[k]
        : this.optionView.effective[k];
    },
    set(k, v) {
      this.overrides = { ...this.overrides, [k]: v };
    },
    reset(k) {
      const next = { ...this.overrides };
      delete next[k], (this.overrides = next);
    },
    complex(k) {
      return JSON.stringify(this.value(k), null, 2);
    },
    setComplex(k, v) {
      try {
        this.set(k, JSON.parse(v));
      } catch {
        this.error = `${k} must contain valid JSON`;
      }
    },
    async saveOptions() {
      await this.perform(async () => {
        const values = this.advanced
          ? JSON.parse(this.rawOptions)
          : this.overrides;
        this.optionView = await api.saveOptions(
          this.selected,
          this.root.id,
          values
        );
        this.overrides = { ...this.optionView.values };
        this.rawOptions = JSON.stringify(this.overrides, null, 2);
      }, "Site overrides saved");
    },
    async saveSite() {
      this.siteSaving = true;
      this.siteSavedAt = null;
      this.error = "";
      this.notice = "";
      try {
        this.detail = await api.updateSite(this.selected, this.root.id, {
          name: this.detail.name,
        });
        this.siteSavedAt = new Date();
        this.notice = "Site record saved";
      } catch (e) {
        this.error = e.message;
      } finally {
        this.siteSaving = false;
      }
    },
    async upgrade() {
      if (!this.upgradeVersionId) return;
      await this.perform(async () => {
        this.detail = await api.updateSite(this.selected, this.root.id, {
          template_version_id: this.upgradeVersionId,
        });
        this.optionView = await api.options(this.selected, this.root.id);
        this.overrides = { ...this.optionView.values };
        this.rawOptions = JSON.stringify(this.overrides, null, 2);
        this.upgradeVersionId = "";
      }, "Template upgraded. Create a new build and deployment to publish it.");
    },
    async create() {
      await this.perform(async () => {
        const rows = this.overrideSeed ? this.seedRecords() : [];
        const created = await api.createChild(this.root.id, {
          name: this.createForm.name,
          slug: this.createForm.slug,
          template_version_id: this.createForm.template_version_id,
        });
        for (const row of rows)
          await api.saveSiteRecord(
            created.id,
            this.root.id,
            row.collection,
            row.key,
            row.value
          );
        this.createForm = { name: "", slug: "", template_version_id: "" };
        this.overrideSeed = false;
        await this.reload();
        this.selected = created.id;
        await this.loadSite();
      }, this.overrideSeed ? "Child site created with replacement seed data" : "Child site created");
    },
    seedRecords() {
      const rows = JSON.parse(this.customSeed);
      if (!Array.isArray(rows))
        throw new Error("Replacement seed must be a JSON array.");
      const seen = new Set();
      rows.forEach((row, index) => {
        if (
          !row ||
          typeof row !== "object" ||
          typeof row.collection !== "string" ||
          !row.collection ||
          typeof row.key !== "string" ||
          !row.key ||
          !row.value ||
          typeof row.value !== "object" ||
          Array.isArray(row.value)
        )
          throw new Error(
            `Seed record ${index + 1} must contain collection, key, and an object value.`
          );
        const identity = `${row.collection}\u0000${row.key}`;
        if (seen.has(identity))
          throw new Error(`Duplicate seed record: ${row.collection}/${row.key}`);
        seen.add(identity);
      });
      return rows;
    },
    async archive() {
      if (
        !confirm(
          "Permanently delete this child site, including its domains, deployments, builds, and data? This cannot be undone."
        )
      )
        return;
      await this.perform(async () => {
        await api.archiveSite(this.selected, this.root.id);
        this.selected = this.root.id;
        await this.reload();
        await this.loadSite();
      }, "Child site permanently deleted");
    },
  },
};
</script>
